import React from 'react';
import User from '../../../data/models/User';
import DataManager from '../../../data/DataManager';
import Modal from '../../ui-elements/Modal';
import { IconStore } from '../../../IconStore';
import {
  fetchSubscription
} from '../../../data/SubscriptionUtils';

const MONTHLY_SUBSCRIPTION_PRICE_USD = 9;

function formatSubscriptionAmount(subscription) {
  if (subscription.amount == null) {
    return MONTHLY_SUBSCRIPTION_PRICE_USD;
  }

  return subscription.amount >= 100 ? subscription.amount / 100 : subscription.amount;
}

function formatRenewalDate(timestamp) {
  if (!timestamp) {
    return null;
  }

  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

function getSubscriptionStatusLabel(subscription) {
  if (!subscription || subscription.status === 'none') {
    return 'No subscription';
  }

  if (subscription.cancelAtPeriodEnd) {
    return subscription.statusLabel || 'Canceling';
  }

  switch (subscription.status) {
    case 'active':
    case 'trialing':
      return 'Active';
    case 'past_due':
      return 'Past due';
    case 'canceled':
    case 'unpaid':
      return 'Canceled';
    default:
      return subscription.status;
  }
}

function getSubscriptionStatusClass(subscription) {
  if (!subscription || subscription.status === 'none') {
    return 'subscription-status-badge badge bg-secondary';
  }

  if (subscription.cancelAtPeriodEnd) {
    return 'subscription-status-badge badge bg-warning text-dark';
  }

  switch (subscription.status) {
    case 'active':
    case 'trialing':
      return 'subscription-status-badge badge bg-success';
    case 'past_due':
      return 'subscription-status-badge badge bg-danger';
    default:
      return 'subscription-status-badge badge bg-secondary';
  }
}

function canCancelSubscription(subscription) {
  if (!subscription || subscription.status === 'none') {
    return false;
  }

  if (subscription.cancelAtPeriodEnd) {
    return false;
  }

  return subscription.status === 'active' || subscription.status === 'trialing' || subscription.status === 'past_due';
}

function isSubscriptionActive(subscription) {
  if (!subscription) {
    return false;
  }

  if (subscription.cancelAtPeriodEnd) {
    return false;
  }

  return subscription.status === 'active' || subscription.status === 'trialing';
}

class SubscriptionInfo extends React.Component {
  constructor(props) {
    super(props);

    const users = User.findAll();
    const user = users.length > 0 ? users[0] : null;
    this.state = {
      user: user,
      subscription: null,
      subscriptionLoading: true,
      subscriptionError: null,
      isCanceling: false,
      cancelError: null,
      isResubscribing: false,
      resubscribeError: null
    };

    this.loadSubscription = this.loadSubscription.bind(this);
    this.confirmCancelSubscription = this.confirmCancelSubscription.bind(this);
    this.resubscribe = this.resubscribe.bind(this);
  }

  componentDidMount() {
    this.loadSubscription();
  }

  loadSubscription() {
    const { user } = this.state;
    this.setState({
      subscriptionLoading: true,
      subscriptionError: null,
      cancelError: null
    });

    return fetchSubscription(user)
      .then((subscription) => {
        this.setState({
          subscription,
          subscriptionLoading: false
        });
      })
      .catch(() => {
        this.setState({
          subscriptionLoading: false,
          subscriptionError: 'Unable to load subscription details. Please try again.'
        });
      });
  }

  confirmCancelSubscription() {
    const { user } = this.state;

    this.setState({
      isCanceling: true,
      cancelError: null
    });

    const dataManager = DataManager.getInstance();
    dataManager.performOperation('cancelSubscription', user)
      .then((updatedSubscription) => {
        this.setState((prevState) => {
          const previousSubscription = prevState.subscription || {};
          const subscription = {
            ...previousSubscription,
            ...updatedSubscription
          };

          if (updatedSubscription.billingPortalUrl == null && previousSubscription.billingPortalUrl) {
            subscription.billingPortalUrl = previousSubscription.billingPortalUrl;
          }

          return {
            subscription,
            isCanceling: false
          };
        });
      })
      .catch(() => {
        this.setState({
          isCanceling: false,
          cancelError: 'Unable to cancel subscription. Please try again.'
        });
      });
  }

  resubscribe() {
    const { user } = this.state;

    this.setState({
      isResubscribing: true,
      resubscribeError: null
    });

    const dataManager = DataManager.getInstance();
    dataManager.performOperation('createSubscriptionCheckout', user)
      .catch(() => {
        this.setState({
          isResubscribing: false,
          resubscribeError: 'Unable to start checkout. Please try again.'
        });
      });
  }

  render() {
    const {
      subscription,
      subscriptionLoading,
      subscriptionError,
      isCanceling,
      cancelError,
      isResubscribing,
      resubscribeError
    } = this.state;

    if (subscriptionLoading) {
      return (
        <div className="card card-body bg-white border-light shadow-sm mb-4">
          <h2 className="h5 mb-3">Subscription</h2>
          <p className="mb-0 text-muted">Loading subscription...</p>
        </div>
      );
    }

    if (subscriptionError) {
      return (
        <div className="card card-body bg-white border-light shadow-sm mb-4">
          <h2 className="h5 mb-3">Subscription</h2>
          <div className="alert alert-danger mb-3" role="alert">{subscriptionError}</div>
          <button type="button" className="btn btn-primary" onClick={this.loadSubscription}>Retry</button>
        </div>
      );
    }

    const subscriptionPrice = formatSubscriptionAmount(subscription);
    const planName = subscription.planName || 'Jashsoft Monthly';
    const renewalDate = formatRenewalDate(subscription.currentPeriodEnd);
    const showCancelButton = canCancelSubscription(subscription);
    const showResubscribeButton = !isSubscriptionActive(subscription);
    const billingPortalUrl = subscription?.billingPortalUrl;

    return (
      <div className="card card-body bg-white border-light shadow-sm mb-4">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <h2 className="h5 mb-0">Subscription</h2>
          <span className={getSubscriptionStatusClass(subscription)}>
            {getSubscriptionStatusLabel(subscription)}
          </span>
        </div>

        {subscription && subscription.status !== 'none' ?
          <div>
            <div className="card border-light shadow-sm mb-3">
              <div className="card-body p-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="mb-1 font-weight-bold">{planName}</p>
                    <p className="mb-0 text-muted small">Portfolio management subscription</p>
                  </div>
                  <div className="text-right">
                    <p className="mb-0 h4">${subscriptionPrice}</p>
                    <p className="mb-0 text-muted small">per month</p>
                  </div>
                </div>
              </div>
            </div>

            {renewalDate && !subscription.cancelAtPeriodEnd &&
              <p className="mb-0 text-muted small">
                {`Next billing date: ${renewalDate}.`}
              </p>}
          </div> :
          <p className="mb-0 text-muted">No active subscription found for this account.</p>}

        {cancelError &&
          <div className="alert alert-danger mt-3 mb-0" role="alert">{cancelError}</div>}

        {resubscribeError &&
          <div className="alert alert-danger mt-3 mb-0" role="alert">{resubscribeError}</div>}

        {(showCancelButton || showResubscribeButton || billingPortalUrl) &&
          <div className="d-flex flex-wrap align-items-center gap-3 mt-3">
            {showCancelButton &&
              <button
                type="button"
                className="btn btn-danger"
                data-toggle="modal"
                data-target="#modal-cancel-subscription"
                disabled={isCanceling}
              >
                {isCanceling ? 'Canceling...' : 'Cancel subscription'}
              </button>}

            {showResubscribeButton &&
              <button
                type="button"
                className="btn btn-success"
                onClick={this.resubscribe}
                disabled={isResubscribing}
              >
                {isResubscribing ? 'Redirecting to checkout...' : 'Re-subscribe'}
              </button>}

            {billingPortalUrl &&
              <a href={billingPortalUrl} className="btn btn-light text-dark ml-auto" target="_blank" rel="noopener noreferrer">
                Manage in Stripe
                <span className={"fas " + IconStore.externalLink + " ml-1"} aria-hidden="true"></span>
              </a>}
          </div>}

        {showCancelButton &&
          <Modal
            modalId="modal-cancel-subscription"
            title="Cancel subscription?"
            bodyElement={(
              <p>
                {renewalDate ?
                  `You will keep access until ${renewalDate}.` :
                  'Are you sure you want to cancel your subscription?'}
              </p>
            )}
            actionButtonTitle="Cancel subscription"
            destructive={true}
            onClick={this.confirmCancelSubscription}
          />}
      </div>
    );
  }
}

export default SubscriptionInfo;
